import { createFileRoute, Link } from '@tanstack/react-router';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  parseTrellisCsv,
  parseCncRouteCsv,
  type TrellisPharmacyGroup,
} from '@lynrx/shared';
import { useAuth } from '../lib/auth/auth-context';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { Dropzone } from '../components/ui/Dropzone';
import { DataTable, type Column } from '../components/ui/DataTable';

export const Route = createFileRoute('/_authenticated/orders/upload')({
  component: OrdersUploadPage,
});

const MAX_FILE_BYTES = 5 * 1024 * 1024;

type InvalidRow = { row_number: number; errors: string[] };

type ParsedRowPreview = {
  patient_name: string;
  patient_phone: string;
  address_line_1: string;
  address_line_2: string | null;
  city: string;
  state: string;
  zip: string;
  order_number: string;
  delivery_notes: string | null;
};

type PreviewResponse = {
  upload_token: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: InvalidRow[];
  preview: ParsedRowPreview[];
  warnings: string[];
};

type CommitResponse = {
  ingested_count: number;
  rejected_count: number;
  audit_event_id: string;
  storage_path: string | null;
  warning?: string;
};

type AutoAssignSummary = {
  routes_created: number;
  routes_updated: number;
  orders_assigned: number;
  orders_no_rule: number;
};

/**
 * Compute the next weekday (Mon-Fri) after `from` in ISO YYYY-MM-DD.
 * Ingest happens the day BEFORE delivery in CNC's nightly cadence; this
 * mirrors that default. Dispatcher can eventually override via a date
 * picker on the upload form (follow-up).
 */
function nextWeekdayISO(from: Date = new Date()): string {
  const d = new Date(from);
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() + 1);
  }
  return d.toISOString().slice(0, 10);
}

/**
 * Fire-and-catch call to /api/orders/auto-assign-routes. Non-fatal —
 * the ingest is already committed; auto-assign is a follow-up that
 * either matches or leaves orders pending for the dispatcher.
 * Returns null on any failure so the caller can render "ingest ok,
 * auto-assign skipped" gracefully.
 */
async function callAutoAssign(
  accessToken: string,
  pharmacyId: string,
  deliveryDate: string,
): Promise<AutoAssignSummary | null> {
  try {
    const res = await fetch('/api/orders/auto-assign-routes', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pharmacy_id: pharmacyId,
        delivery_date: deliveryDate,
      }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      Sentry.captureMessage('orders.upload.auto_assign.non_2xx', {
        level: 'warning',
        tags: {
          status: String(res.status),
          error_type: body.error ?? 'unknown',
        },
      });
      return null;
    }
    return (await res.json()) as AutoAssignSummary;
  } catch (err) {
    Sentry.captureException(err, { tags: { area: 'orders.upload.auto_assign' } });
    return null;
  }
}

type PharmacyOption = { id: string; name: string };

/**
 * Which parser + upload path this page uses.
 * - lynrx:            canonical CSV format, one pharmacy per upload.
 * - trellis:          raw Trellis email attachment, multi-pharmacy + DriverID filter.
 * - cnc-driver-route: CNC post-processed per-driver route CSV — already
 *                     single-driver, grouped by the Pharmacy column.
 */
type SourceFormat = 'lynrx' | 'trellis' | 'cnc-driver-route';

/** Per-pharmacy commit outcome inside a Trellis multi-pharmacy upload. */
type TrellisPharmacyOutcome =
  | { origin_name: string; status: 'pending' }
  | { origin_name: string; status: 'in_progress' }
  | {
      origin_name: string;
      status: 'success';
      ingested: number;
      rejected: number;
      autoAssign: AutoAssignSummary | null;
    }
  | { origin_name: string; status: 'error'; message: string };

type Stage =
  | { kind: 'empty' }
  | { kind: 'preview'; file: File; data: PreviewResponse }
  | {
      kind: 'success';
      ingestedCount: number;
      autoAssign: AutoAssignSummary | null;
      deliveryDate: string;
    }
  | {
      kind: 'trellis-preview';
      file: File;
      groups: TrellisPharmacyGroup[];
      totalRows: number;
      filteredOut: number;
      invalidCount: number;
      warnings: string[];
      /** Per-group user-selected LYN Rx pharmacy_id (or null until picked). */
      groupMappings: Record<string, string | null>;
    }
  | {
      kind: 'trellis-committing';
      file: File;
      groups: TrellisPharmacyGroup[];
      groupMappings: Record<string, string>;
      outcomes: TrellisPharmacyOutcome[];
    }
  | {
      kind: 'trellis-done';
      totalIngested: number;
      outcomes: TrellisPharmacyOutcome[];
      deliveryDate: string;
    };

type ApiError = { status: number; code: string };

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const PREVIEW_COLUMNS: Column<ParsedRowPreview>[] = [
  { key: 'order_number', header: 'Order #', render: (r) => r.order_number },
  { key: 'patient_name', header: 'Patient', render: (r) => r.patient_name },
  {
    key: 'address',
    header: 'Address',
    render: (r) => [r.address_line_1, r.address_line_2].filter(Boolean).join(' '),
  },
  { key: 'city', header: 'City', render: (r) => r.city },
  { key: 'state', header: 'State', render: (r) => r.state },
  { key: 'zip', header: 'ZIP', render: (r) => r.zip },
];

export function OrdersUploadPage(): JSX.Element {
  const { session } = useAuth();
  const accessToken = session?.access_token ?? null;
  const currentUserId = session?.user?.id ?? null;

  useSetPageHeader({
    title: 'Upload orders',
    subtitle:
      'Upload a CSV of deliveries. Valid rows are ingested as pending orders.',
    parentLabel: 'Orders',
    parentTo: '/orders',
  });

  const [stage, setStage] = useState<Stage>({ kind: 'empty' });
  const [error, setError] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pharmacies, setPharmacies] = useState<PharmacyOption[] | null>(null);
  const [selectedPharmacyId, setSelectedPharmacyId] = useState<string | null>(null);
  // Pinned to CNC driver route — the only format CNC (Client #1) uses.
  // Other formats (lynrx canonical, trellis) still have code paths and
  // handlers; re-expose the selector (and switch back to useState with
  // a setter) when a second pharmacy onboards with a different input
  // format. See notes above on SourceFormat.
  const [sourceFormat] = useState<SourceFormat>('cnc-driver-route');
  const [driverIdFilter, setDriverIdFilter] = useState<string>('');

  // Load the caller's pharmacy_access rows once. Explicit user_id
  // filter is required: for platform_admin callers, RLS returns ALL
  // grants across every tenant (see pharmacy_access_select policy),
  // which floods the dropdown with unrelated pharmacies. Filtering by
  // user_id constrains it to the caller's own grants, matching the
  // intent for pharmacy_staff / dispatcher / pharmacy_admin roles.
  // A dedupe pass on pharmacy_id defends against any historical
  // duplicate access rows (the unique constraint on
  // (user_id, pharmacy_id) should prevent this, but the dropdown is
  // the last mile — cheap to guard).
  useEffect(() => {
    if (!accessToken || !currentUserId) return;
    let cancelled = false;
    void supabase
      .from('pharmacy_access')
      .select('pharmacy_id, pharmacies(name)')
      .eq('user_id', currentUserId)
      .then(({ data, error: err }) => {
        if (cancelled) return;
        if (err) {
          Sentry.captureException(err, {
            tags: { area: 'orders.upload.pharmacy_access_load' },
          });
          setPharmacies([]);
          return;
        }
        const seen = new Set<string>();
        const list: PharmacyOption[] = [];
        for (const row of data ?? []) {
          if (seen.has(row.pharmacy_id)) continue;
          seen.add(row.pharmacy_id);
          list.push({
            id: row.pharmacy_id,
            name: row.pharmacies?.name ?? row.pharmacy_id,
          });
        }
        setPharmacies(list);
        if (list.length === 1 && list[0]) setSelectedPharmacyId(list[0].id);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, currentUserId]);

  const showPharmacySelect = (pharmacies?.length ?? 0) > 1;
  const noPharmacyAccess = pharmacies !== null && pharmacies.length === 0;

  const resetToEmpty = useCallback(() => {
    setStage({ kind: 'empty' });
    setError(null);
  }, []);

  const handleTrellisFile = useCallback(
    async (file: File) => {
      setError(null);
      if (file.size > MAX_FILE_BYTES) {
        setError({ status: 0, code: 'file_too_large' });
        return;
      }
      if (pharmacies === null) {
        setError({ status: 0, code: 'pharmacies_loading' });
        return;
      }
      setSubmitting(true);
      try {
        const text = await file.text();
        const opts = driverIdFilter.trim() !== ''
          ? { driver_id_filter: driverIdFilter.trim() }
          : {};
        const result = parseTrellisCsv(text, opts);
        if (!result.ok) {
          setError({ status: 0, code: `trellis_parse:${result.file_errors[0] ?? 'unknown'}` });
          return;
        }
        if (result.pharmacies.length === 0) {
          setError({
            status: 0,
            code:
              driverIdFilter.trim() !== ''
                ? `trellis_no_rows_after_filter:${result.filtered_out} rows dropped by DriverID filter`
                : 'trellis_no_valid_rows',
          });
          return;
        }
        // Auto-map each OriginName to a LYN Rx pharmacy_id by
        // case-insensitive exact-name match. User can override in the
        // preview stage. Unmatched groups start as null (must be picked
        // before commit).
        const mappings: Record<string, string | null> = {};
        for (const group of result.pharmacies) {
          const match = pharmacies.find(
            (p) => p.name.trim().toLowerCase() === group.origin_name.trim().toLowerCase(),
          );
          mappings[group.origin_name] = match?.id ?? null;
        }
        setStage({
          kind: 'trellis-preview',
          file,
          groups: result.pharmacies,
          totalRows: result.total_rows,
          filteredOut: result.filtered_out,
          invalidCount: result.invalid.length,
          warnings: result.warnings,
          groupMappings: mappings,
        });
      } catch (err) {
        Sentry.captureException(err, { tags: { area: 'orders.upload.trellis.parse' } });
        setError({ status: 0, code: 'trellis_parse_exception' });
      } finally {
        setSubmitting(false);
      }
    },
    [driverIdFilter, pharmacies],
  );

  const handleCncRouteFile = useCallback(
    async (file: File) => {
      setError(null);
      if (file.size > MAX_FILE_BYTES) {
        setError({ status: 0, code: 'file_too_large' });
        return;
      }
      if (pharmacies === null) {
        setError({ status: 0, code: 'pharmacies_loading' });
        return;
      }
      setSubmitting(true);
      try {
        const text = await file.text();
        const result = parseCncRouteCsv(text);
        if (!result.ok) {
          setError({ status: 0, code: `cnc_parse:${result.file_errors[0] ?? 'unknown'}` });
          return;
        }
        if (result.pharmacies.length === 0) {
          setError({ status: 0, code: 'cnc_no_valid_rows' });
          return;
        }
        // Shape-map to TrellisPharmacyGroup so we can reuse the existing
        // multi-pharmacy preview/commit stages. `origin_name` here is
        // the Pharmacy column value (e.g. "SHSP").
        const groups: TrellisPharmacyGroup[] = result.pharmacies.map((p) => ({
          origin_name: p.pharmacy_name,
          row_count: p.row_count,
          cold_chain_count: p.cold_chain_count,
          synthetic_csv: p.synthetic_csv,
        }));
        const mappings: Record<string, string | null> = {};
        for (const group of groups) {
          const match = pharmacies.find(
            (p) => p.name.trim().toLowerCase() === group.origin_name.trim().toLowerCase(),
          );
          mappings[group.origin_name] = match?.id ?? null;
        }
        setStage({
          kind: 'trellis-preview',
          file,
          groups,
          totalRows: result.total_rows,
          filteredOut: 0,
          invalidCount: result.invalid.length,
          warnings: result.warnings,
          groupMappings: mappings,
        });
      } catch (err) {
        Sentry.captureException(err, { tags: { area: 'orders.upload.cnc.parse' } });
        setError({ status: 0, code: 'cnc_parse_exception' });
      } finally {
        setSubmitting(false);
      }
    },
    [pharmacies],
  );

  const handleTrellisCommit = useCallback(async () => {
    if (stage.kind !== 'trellis-preview') return;
    if (!accessToken) {
      setError({ status: 0, code: 'not_authenticated' });
      return;
    }
    // Every group must have a mapping before we start.
    const finalized: Record<string, string> = {};
    for (const group of stage.groups) {
      const mapped = stage.groupMappings[group.origin_name];
      if (!mapped) {
        setError({ status: 0, code: 'trellis_mapping_incomplete' });
        return;
      }
      finalized[group.origin_name] = mapped;
    }
    setError(null);
    const initialOutcomes: TrellisPharmacyOutcome[] = stage.groups.map((g) => ({
      origin_name: g.origin_name,
      status: 'pending',
    }));
    setStage({
      kind: 'trellis-committing',
      file: stage.file,
      groups: stage.groups,
      groupMappings: finalized,
      outcomes: initialOutcomes,
    });

    // Snapshot the values we need — setStage above is async, but the
    // loop below only reads these locals.
    const groupsLocal = stage.groups;
    const fileNameLocal = stage.file.name;
    const outcomes = [...initialOutcomes];
    // Single delivery date across all per-pharmacy commits in this
    // batch. Defaults to the next weekday; date-picker override is a
    // follow-up.
    const deliveryDate = nextWeekdayISO();

    for (let i = 0; i < groupsLocal.length; i++) {
      const group = groupsLocal[i]!;
      const pharmacyId = finalized[group.origin_name]!;
      outcomes[i] = { origin_name: group.origin_name, status: 'in_progress' };
      setStage((prev) =>
        prev.kind === 'trellis-committing' ? { ...prev, outcomes: [...outcomes] } : prev,
      );

      try {
        // Synthesize a File from the pharmacy group's canonical CSV,
        // preserving the source filename with a per-pharmacy suffix so
        // the archived CSV in Storage is traceable back to the Trellis
        // batch.
        const syntheticFile = new File(
          [group.synthetic_csv],
          `${fileNameLocal.replace(/\.csv$/i, '')}--${group.origin_name.replace(/\s+/g, '_')}.csv`,
          { type: 'text/csv' },
        );
        const formData = new FormData();
        formData.set('file', syntheticFile);
        formData.set('pharmacy_id', pharmacyId);
        const previewRes = await fetch('/api/orders/upload/preview', {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
          body: formData,
        });
        if (!previewRes.ok) {
          const body = (await previewRes.json().catch(() => ({}))) as { error?: string };
          outcomes[i] = {
            origin_name: group.origin_name,
            status: 'error',
            message: `preview failed: ${body.error ?? previewRes.status}`,
          };
          setStage((prev) =>
            prev.kind === 'trellis-committing' ? { ...prev, outcomes: [...outcomes] } : prev,
          );
          continue;
        }
        const previewData = (await previewRes.json()) as PreviewResponse;

        const commitRes = await fetch('/api/orders/upload/commit', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            upload_token: previewData.upload_token,
            pharmacy_id: pharmacyId,
          }),
        });
        if (!commitRes.ok) {
          const body = (await commitRes.json().catch(() => ({}))) as { error?: string };
          outcomes[i] = {
            origin_name: group.origin_name,
            status: 'error',
            message: `commit failed: ${body.error ?? commitRes.status}`,
          };
          setStage((prev) =>
            prev.kind === 'trellis-committing' ? { ...prev, outcomes: [...outcomes] } : prev,
          );
          continue;
        }
        const commitData = (await commitRes.json()) as CommitResponse;
        // Fire per-pharmacy auto-assign after commit. Non-fatal — if
        // no rules exist for this pharmacy the summary is all zeros
        // (or null on any HTTP failure) and the dispatcher falls
        // through to manual route building for this pharmacy's orders.
        const autoAssign = await callAutoAssign(accessToken, pharmacyId, deliveryDate);
        outcomes[i] = {
          origin_name: group.origin_name,
          status: 'success',
          ingested: commitData.ingested_count,
          rejected: commitData.rejected_count,
          autoAssign,
        };
        setStage((prev) =>
          prev.kind === 'trellis-committing' ? { ...prev, outcomes: [...outcomes] } : prev,
        );
      } catch (err) {
        Sentry.captureException(err, {
          tags: { area: 'orders.upload.trellis.commit_loop', origin_name: group.origin_name },
        });
        outcomes[i] = {
          origin_name: group.origin_name,
          status: 'error',
          message: 'network error',
        };
        setStage((prev) =>
          prev.kind === 'trellis-committing' ? { ...prev, outcomes: [...outcomes] } : prev,
        );
      }
    }

    const totalIngested = outcomes.reduce(
      (acc, o) => acc + (o.status === 'success' ? o.ingested : 0),
      0,
    );
    setStage({ kind: 'trellis-done', totalIngested, outcomes, deliveryDate });
  }, [accessToken, stage]);

  const handleFile = useCallback(
    async (file: File) => {
      if (sourceFormat === 'trellis') {
        await handleTrellisFile(file);
        return;
      }
      if (sourceFormat === 'cnc-driver-route') {
        await handleCncRouteFile(file);
        return;
      }
      setError(null);
      if (file.size > MAX_FILE_BYTES) {
        setError({ status: 0, code: 'file_too_large' });
        return;
      }
      if (!accessToken) {
        setError({ status: 0, code: 'not_authenticated' });
        return;
      }
      if (!selectedPharmacyId) {
        setError({ status: 0, code: 'pharmacy_required' });
        return;
      }
      setSubmitting(true);
      const formData = new FormData();
      formData.set('file', file);
      formData.set('pharmacy_id', selectedPharmacyId);
      try {
        const res = await fetch('/api/orders/upload/preview', {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
          body: formData,
        });
        if (!res.ok) {
          let code = 'preview_failed';
          try {
            const body = (await res.json()) as { error?: string };
            if (typeof body.error === 'string') code = body.error;
          } catch {
            // body not JSON; keep generic code
          }
          Sentry.captureMessage('orders.upload.preview.non_2xx', {
            level: 'error',
            tags: { status: String(res.status), error_type: code },
          });
          setError({ status: res.status, code });
          return;
        }
        const data = (await res.json()) as PreviewResponse;
        setStage({ kind: 'preview', file, data });
      } catch (err) {
        Sentry.captureException(err, { tags: { area: 'orders.upload.preview.network' } });
        setError({ status: 0, code: 'network_error' });
      } finally {
        setSubmitting(false);
      }
    },
    [accessToken, selectedPharmacyId, sourceFormat, handleTrellisFile, handleCncRouteFile],
  );

  const handleCommit = useCallback(async () => {
    if (stage.kind !== 'preview') return;
    if (!accessToken || !selectedPharmacyId) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/orders/upload/commit', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          upload_token: stage.data.upload_token,
          pharmacy_id: selectedPharmacyId,
        }),
      });
      if (!res.ok) {
        let code = 'commit_failed';
        try {
          const body = (await res.json()) as { error?: string };
          if (typeof body.error === 'string') code = body.error;
        } catch {
          // ignore
        }
        Sentry.captureMessage('orders.upload.commit.non_2xx', {
          level: 'error',
          tags: { status: String(res.status), error_type: code },
        });
        setError({ status: res.status, code });
        return;
      }
      const data = (await res.json()) as CommitResponse;
      // Fire routing-rule auto-assign for the next weekday. Non-fatal
      // — if no rules exist or the call fails, the orders are still
      // successfully ingested; the dispatcher just builds routes
      // manually as before.
      const deliveryDate = nextWeekdayISO();
      const autoAssign = await callAutoAssign(
        accessToken!,
        selectedPharmacyId!,
        deliveryDate,
      );
      setStage({
        kind: 'success',
        ingestedCount: data.ingested_count,
        autoAssign,
        deliveryDate,
      });
    } catch (err) {
      Sentry.captureException(err, { tags: { area: 'orders.upload.commit.network' } });
      setError({ status: 0, code: 'network_error' });
    } finally {
      setSubmitting(false);
    }
  }, [accessToken, selectedPharmacyId, stage]);

  const friendlyError = useMemo(() => {
    if (!error) return null;
    if (error.code === 'file_too_large') {
      return 'That file is larger than 5 MB. Please upload a smaller CSV.';
    }
    if (error.code === 'pharmacy_required') {
      return 'Choose a pharmacy before uploading.';
    }
    if (error.code === 'not_authenticated') {
      return 'Your session expired. Please sign in again.';
    }
    if (error.code === 'network_error') {
      return 'Could not reach the server. Check your connection and try again.';
    }
    if (error.code === 'pharmacies_loading') {
      return 'Pharmacy list still loading. Try again in a moment.';
    }
    if (error.code === 'trellis_mapping_incomplete') {
      return 'Every pharmacy in the CSV must be mapped to a LYN Rx pharmacy before commit.';
    }
    if (error.code === 'trellis_no_valid_rows') {
      return 'No valid rows in the Trellis CSV after transform.';
    }
    if (error.code.startsWith('trellis_no_rows_after_filter:')) {
      return `Your DriverID filter matched no rows in this file. (${error.code.slice('trellis_no_rows_after_filter:'.length)})`;
    }
    if (error.code.startsWith('trellis_parse:')) {
      return `Trellis CSV parse failed: ${error.code.slice('trellis_parse:'.length)}`;
    }
    if (error.code === 'trellis_parse_exception') {
      return 'Trellis CSV could not be read. Please verify the file and try again.';
    }
    if (error.code === 'cnc_no_valid_rows') {
      return 'No valid rows in the CNC driver-route CSV.';
    }
    if (error.code.startsWith('cnc_parse:')) {
      return `CNC driver-route CSV parse failed: ${error.code.slice('cnc_parse:'.length)}`;
    }
    if (error.code === 'cnc_parse_exception') {
      return 'CNC driver-route CSV could not be read. Please verify the file and try again.';
    }
    const suffix = error.status > 0 ? ` (HTTP ${error.status})` : '';
    return `Upload failed: ${error.code}${suffix}`;
  }, [error]);

  return (
    <div className="mx-auto max-w-3xl">
        {noPharmacyAccess ? (
          <Alert tone="error" title="No pharmacy access">
            You don&apos;t have access to upload orders for any pharmacy. Ask an admin to grant access.
          </Alert>
        ) : null}

        {/* CSV source-format selector removed while CNC is the only
            active pharmacy — sourceFormat is pinned to
            'cnc-driver-route' in the state initializer above.
            Restore this <Card> (radios + optional Trellis DriverID
            filter) when a second pharmacy needs the LYN Rx canonical
            or Trellis input path. */}

        {showPharmacySelect && sourceFormat === 'lynrx' ? (
          <Card className="mb-6">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink">Pharmacy</span>
              <select
                value={selectedPharmacyId ?? ''}
                onChange={(e) => setSelectedPharmacyId(e.target.value || null)}
                className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select a pharmacy…</option>
                {pharmacies?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          </Card>
        ) : null}

        {friendlyError ? (
          <div className="mb-6">
            <Alert tone="error" title="Something went wrong">
              <div className="flex items-center justify-between gap-3">
                <span>{friendlyError}</span>
                <Button variant="secondary" onClick={() => setError(null)}>
                  Try again
                </Button>
              </div>
            </Alert>
          </div>
        ) : null}

        {stage.kind === 'empty' ? (
          <EmptyStage
            disabled={
              submitting ||
              noPharmacyAccess ||
              (sourceFormat === 'lynrx' && !selectedPharmacyId)
            }
            onFile={handleFile}
            submitting={submitting}
          />
        ) : null}

        {stage.kind === 'preview' ? (
          <PreviewStage
            file={stage.file}
            data={stage.data}
            submitting={submitting}
            onCommit={handleCommit}
            onCancel={resetToEmpty}
          />
        ) : null}

        {stage.kind === 'success' ? (
          <SuccessStage
            ingestedCount={stage.ingestedCount}
            autoAssign={stage.autoAssign}
            deliveryDate={stage.deliveryDate}
            onUploadAnother={resetToEmpty}
          />
        ) : null}

        {stage.kind === 'trellis-preview' ? (
          <TrellisPreviewStage
            file={stage.file}
            groups={stage.groups}
            totalRows={stage.totalRows}
            filteredOut={stage.filteredOut}
            invalidCount={stage.invalidCount}
            warnings={stage.warnings}
            groupMappings={stage.groupMappings}
            pharmacies={pharmacies ?? []}
            onMappingChange={(originName, pharmacyId) => {
              setStage((prev) =>
                prev.kind === 'trellis-preview'
                  ? {
                      ...prev,
                      groupMappings: { ...prev.groupMappings, [originName]: pharmacyId },
                    }
                  : prev,
              );
            }}
            onCommit={handleTrellisCommit}
            onCancel={resetToEmpty}
          />
        ) : null}

        {stage.kind === 'trellis-committing' ? (
          <TrellisCommittingStage outcomes={stage.outcomes} />
        ) : null}

        {stage.kind === 'trellis-done' ? (
          <TrellisDoneStage
            totalIngested={stage.totalIngested}
            outcomes={stage.outcomes}
            deliveryDate={stage.deliveryDate}
            onUploadAnother={resetToEmpty}
          />
        ) : null}
    </div>
  );
}

function EmptyStage({
  disabled,
  onFile,
  submitting,
}: {
  disabled: boolean;
  onFile: (file: File) => void;
  submitting: boolean;
}): JSX.Element {
  return (
    <Card>
      <Dropzone disabled={disabled} onFile={onFile} />
      <div className="mt-4 flex items-center justify-between text-sm">
        <a
          href="/sample-orders.csv"
          download
          className="text-primary hover:underline"
        >
          Download a sample CSV
        </a>
        {submitting ? <span className="text-ink-2">Uploading…</span> : null}
      </div>
    </Card>
  );
}

function PreviewStage({
  file,
  data,
  submitting,
  onCommit,
  onCancel,
}: {
  file: File;
  data: PreviewResponse;
  submitting: boolean;
  onCommit: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [showRejected, setShowRejected] = useState(false);
  const canCommit = data.valid_rows > 0;
  return (
    <Card>
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div>
          <p className="text-sm font-medium text-ink">{file.name}</p>
          <p className="text-xs text-ink-2">{formatBytes(file.size)}</p>
        </div>
        <p className="text-sm font-medium text-ink">
          {data.valid_rows} of {data.total_rows} rows valid
        </p>
      </div>

      <div className="mt-4">
        <h2 className="mb-2 text-sm font-medium text-ink">Preview (first 5 valid rows)</h2>
        <DataTable
          columns={PREVIEW_COLUMNS}
          rows={data.preview}
          rowKey={(r) => r.order_number}
          emptyMessage="No valid rows to preview."
        />
      </div>

      {data.invalid_rows.length > 0 ? (
        <div className="mt-4">
          <button
            type="button"
            className="text-sm font-medium text-ink underline-offset-4 hover:underline"
            aria-expanded={showRejected}
            onClick={() => setShowRejected((v) => !v)}
          >
            {showRejected ? 'Hide' : 'Show'} {data.invalid_rows.length} rejected row
            {data.invalid_rows.length === 1 ? '' : 's'}
          </button>
          {showRejected ? (
            <ul className="mt-2 space-y-2 rounded-md border border-border bg-bg-soft p-3 text-sm">
              {data.invalid_rows.map((row) => (
                <li key={row.row_number} className="text-ink">
                  <span className="font-medium">Row {row.row_number}:</span>{' '}
                  <span className="text-ink-2">{row.errors.join('; ')}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="mt-6 flex items-center justify-end gap-3">
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={onCommit} disabled={!canCommit || submitting} loading={submitting}>
          Commit valid rows
        </Button>
      </div>
    </Card>
  );
}

function SuccessStage({
  ingestedCount,
  autoAssign,
  deliveryDate,
  onUploadAnother,
}: {
  ingestedCount: number;
  autoAssign: AutoAssignSummary | null;
  deliveryDate: string;
  onUploadAnother: () => void;
}): JSX.Element {
  return (
    <Card>
      <div className="flex flex-col items-start gap-4">
        <p className="text-lg font-medium text-emerald-700">
          ✓ {ingestedCount} order{ingestedCount === 1 ? '' : 's'} ingested
        </p>
        <AutoAssignSummaryLine
          autoAssign={autoAssign}
          deliveryDate={deliveryDate}
        />
        <div className="flex items-center gap-3">
          <Link
            to="/orders"
            search={{ status: 'pending' }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
          >
            View orders
          </Link>
          <Button variant="secondary" onClick={onUploadAnother}>
            Upload another
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * One-line auto-assign summary shared by both success stages.
 * autoAssign === null means the call failed or wasn't attempted (rare
 * network issue) — we still show the ingest as successful and prompt
 * the dispatcher to build routes manually.
 */
function AutoAssignSummaryLine({
  autoAssign,
  deliveryDate,
}: {
  autoAssign: AutoAssignSummary | null;
  deliveryDate: string;
}): JSX.Element {
  if (autoAssign === null) {
    return (
      <p className="text-sm text-ink-2">
        Auto-assign wasn&apos;t reached. Orders are pending — build routes
        manually.
      </p>
    );
  }
  const totalCreatedOrUpdated =
    autoAssign.routes_created + autoAssign.routes_updated;
  if (autoAssign.orders_assigned === 0 && totalCreatedOrUpdated === 0) {
    return (
      <p className="text-sm text-ink-2">
        No routing rules matched. All {autoAssign.orders_no_rule} order
        {autoAssign.orders_no_rule === 1 ? '' : 's'} remain pending for
        {' '}{deliveryDate}.
      </p>
    );
  }
  return (
    <p className="text-sm text-ink">
      Auto-assigned {autoAssign.orders_assigned} order
      {autoAssign.orders_assigned === 1 ? '' : 's'} to{' '}
      {autoAssign.routes_created} new + {autoAssign.routes_updated} existing
      route{totalCreatedOrUpdated === 1 ? '' : 's'} for {deliveryDate}
      {autoAssign.orders_no_rule > 0
        ? `. ${autoAssign.orders_no_rule} unmatched (still pending).`
        : '.'}
    </p>
  );
}

function TrellisPreviewStage({
  file,
  groups,
  totalRows,
  filteredOut,
  invalidCount,
  warnings,
  groupMappings,
  pharmacies,
  onMappingChange,
  onCommit,
  onCancel,
}: {
  file: File;
  groups: TrellisPharmacyGroup[];
  totalRows: number;
  filteredOut: number;
  invalidCount: number;
  warnings: string[];
  groupMappings: Record<string, string | null>;
  pharmacies: PharmacyOption[];
  onMappingChange: (originName: string, pharmacyId: string | null) => void;
  onCommit: () => void;
  onCancel: () => void;
}): JSX.Element {
  const allMapped = groups.every((g) => groupMappings[g.origin_name]);
  const totalColdChain = groups.reduce((acc, g) => acc + g.cold_chain_count, 0);
  return (
    <Card>
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div>
          <p className="text-sm font-medium text-ink">{file.name}</p>
          <p className="text-xs text-ink-2">
            Trellis · {formatBytes(file.size)}
          </p>
        </div>
        <p className="text-sm font-medium text-ink">
          {totalRows} row{totalRows === 1 ? '' : 's'} across {groups.length} pharmac
          {groups.length === 1 ? 'y' : 'ies'}
          {totalColdChain > 0 ? ` · ${totalColdChain} cold chain` : ''}
        </p>
      </div>

      {filteredOut > 0 ? (
        <p className="mt-3 text-xs text-ink-2">
          {filteredOut} row{filteredOut === 1 ? '' : 's'} dropped by the DriverID filter.
        </p>
      ) : null}
      {invalidCount > 0 ? (
        <p className="mt-1 text-xs text-ink-2">
          {invalidCount} row{invalidCount === 1 ? '' : 's'} skipped due to missing Trellis fields.
        </p>
      ) : null}
      {warnings.length > 0 ? (
        <ul className="mt-2 space-y-1 text-xs text-ink-2">
          {warnings.map((w, i) => (
            <li key={i}>· {w}</li>
          ))}
        </ul>
      ) : null}

      <div className="mt-4">
        <h2 className="mb-2 text-sm font-medium text-ink">
          Pharmacy mapping — confirm the LYN Rx pharmacy for each Trellis OriginName
        </h2>
        <div className="space-y-3">
          {groups.map((group) => {
            const mapped = groupMappings[group.origin_name] ?? null;
            return (
              <div
                key={group.origin_name}
                className="flex flex-wrap items-center gap-4 rounded-md border border-border bg-bg-soft p-3"
              >
                <div className="min-w-[10rem] flex-1">
                  <p className="text-sm font-medium text-ink">{group.origin_name}</p>
                  <p className="text-xs text-ink-2">
                    {group.row_count} row{group.row_count === 1 ? '' : 's'}
                    {group.cold_chain_count > 0
                      ? ` · ${group.cold_chain_count} cold chain`
                      : ''}
                  </p>
                </div>
                <select
                  value={mapped ?? ''}
                  onChange={(e) => onMappingChange(group.origin_name, e.target.value || null)}
                  className="min-w-[16rem] rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select LYN Rx pharmacy…</option>
                  {pharmacies.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onCommit} disabled={!allMapped}>
          Commit all pharmacies
        </Button>
      </div>
    </Card>
  );
}

function TrellisCommittingStage({
  outcomes,
}: {
  outcomes: TrellisPharmacyOutcome[];
}): JSX.Element {
  return (
    <Card>
      <p className="text-sm font-medium text-ink">Committing pharmacies…</p>
      <p className="text-xs text-ink-2 mt-1">
        Each pharmacy is uploaded as a separate LYN Rx batch. Do not close this
        tab until all rows finish.
      </p>
      <ul className="mt-4 space-y-2">
        {outcomes.map((o) => (
          <li
            key={o.origin_name}
            className="flex items-center justify-between rounded-md border border-border bg-bg-soft p-3 text-sm"
          >
            <span className="font-medium text-ink">{o.origin_name}</span>
            <span className="text-ink-2">
              {o.status === 'pending' && '⋯ queued'}
              {o.status === 'in_progress' && '⟳ uploading…'}
              {o.status === 'success' &&
                `✓ ${o.ingested} ingested${o.rejected ? `, ${o.rejected} rejected` : ''}`}
              {o.status === 'error' && `✗ ${o.message}`}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function TrellisDoneStage({
  totalIngested,
  outcomes,
  deliveryDate,
  onUploadAnother,
}: {
  totalIngested: number;
  outcomes: TrellisPharmacyOutcome[];
  deliveryDate: string;
  onUploadAnother: () => void;
}): JSX.Element {
  const errorCount = outcomes.filter((o) => o.status === 'error').length;
  // Aggregate per-pharmacy auto-assign summaries across the batch. Any
  // pharmacy whose autoAssign is null (network flake) contributes 0.
  const totalAutoAssigned = outcomes.reduce((acc, o) => {
    if (o.status !== 'success' || !o.autoAssign) return acc;
    return acc + o.autoAssign.orders_assigned;
  }, 0);
  const totalRoutesCreated = outcomes.reduce((acc, o) => {
    if (o.status !== 'success' || !o.autoAssign) return acc;
    return acc + o.autoAssign.routes_created;
  }, 0);
  const totalUnmatched = outcomes.reduce((acc, o) => {
    if (o.status !== 'success' || !o.autoAssign) return acc;
    return acc + o.autoAssign.orders_no_rule;
  }, 0);
  return (
    <Card>
      <div className="flex flex-col items-start gap-4">
        <p className="text-lg font-medium text-emerald-700">
          ✓ {totalIngested} order{totalIngested === 1 ? '' : 's'} ingested across{' '}
          {outcomes.length} pharmac{outcomes.length === 1 ? 'y' : 'ies'}
        </p>
        {totalAutoAssigned + totalRoutesCreated > 0 ? (
          <p className="text-sm text-ink">
            Auto-assigned {totalAutoAssigned} order
            {totalAutoAssigned === 1 ? '' : 's'} to {totalRoutesCreated} new
            route{totalRoutesCreated === 1 ? '' : 's'} for {deliveryDate}
            {totalUnmatched > 0
              ? `. ${totalUnmatched} unmatched (still pending).`
              : '.'}
          </p>
        ) : (
          <p className="text-sm text-ink-2">
            No routing rules matched. Orders remain pending for{' '}
            {deliveryDate} — build routes manually.
          </p>
        )}
        {errorCount > 0 ? (
          <Alert tone="error" title={`${errorCount} pharmacy commit failed`}>
            <ul className="text-sm">
              {outcomes
                .filter((o) => o.status === 'error')
                .map((o) => (
                  <li key={o.origin_name}>
                    <span className="font-medium">{o.origin_name}:</span>{' '}
                    {(o as Extract<TrellisPharmacyOutcome, { status: 'error' }>).message}
                  </li>
                ))}
            </ul>
          </Alert>
        ) : null}
        <ul className="w-full space-y-1 text-sm text-ink-2">
          {outcomes.map((o) => (
            <li key={o.origin_name}>
              {o.status === 'success'
                ? `${o.origin_name} — ${o.ingested} ingested${o.rejected ? `, ${o.rejected} rejected` : ''}${o.autoAssign ? ` · ${o.autoAssign.orders_assigned} auto-assigned` : ''}`
                : o.status === 'error'
                  ? `${o.origin_name} — failed`
                  : `${o.origin_name} — ${o.status}`}
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-3">
          <Link
            to="/orders"
            search={{ status: 'pending' }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
          >
            View orders
          </Link>
          <Button variant="secondary" onClick={onUploadAnother}>
            Upload another
          </Button>
        </div>
      </div>
    </Card>
  );
}
